import { LockSimple, Plus, Shield } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { PERMISSIONS } from "@plannello/shared";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { type Role, rolesQuery } from "@/components/admin/api";
import { useAdminCaps } from "@/components/admin/caps";
import { RoleDrawer } from "@/components/admin/RoleDrawer";
import { drawerSearch, plural, QueryBoundary, roleName, useDrawerParam } from "@/components/admin/shared";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import { Badge, Button, type DataColumn, DataTable, TableSkeleton } from "@/components/ui";
import { loc } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/roles")({
  validateSearch: drawerSearch("role"),
  component: RolesPage,
});

function RolesPage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.adm_nav_roles() }]);
  const q = useQuery(rolesQuery);
  const caps = useAdminCaps();
  const [openId, setOpenId] = useDrawerParam("role");
  const [template, setTemplate] = useState<Role | null>(null);

  const open = (id: string, from?: Role) => {
    setTemplate(from ?? null);
    setOpenId(id);
  };

  const columns: DataColumn<Role>[] = [
    {
      id: "name",
      header: m.adm_role_col_role(),
      isRowHeader: true,
      className: "min-w-56",
      cell: (r) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-control border border-line bg-canvas text-fg-muted">
            {r.builtin ? <LockSimple size={13} aria-hidden="true" /> : <Shield size={13} aria-hidden="true" />}
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="flex items-center gap-1.5">
              <span className="truncate-title font-medium text-fg" title={roleName(r)}>
                {roleName(r)}
              </span>
              {r.builtin ? <Badge>{m.adm_role_builtin()}</Badge> : null}
            </span>
            {r.description ? <span className="truncate-title text-xs text-fg-muted">{loc(r.description)}</span> : null}
          </div>
        </div>
      ),
    },
    {
      id: "permissions",
      header: m.adm_role_permissions(),
      className: "w-36 whitespace-nowrap",
      cell: (r) => (
        <span className="text-fg-muted tnum">
          {m.adm_role_permission_count({ count: r.permissions.length, total: PERMISSIONS.length })}
        </span>
      ),
    },
    {
      id: "used",
      header: m.adm_role_col_used(),
      className: "hidden w-32 sm:table-cell",
      cell: (r) =>
        r.slug === "owner" ? (
          <span className="text-fg-muted">{m.adm_role_owner_flag()}</span>
        ) : (
          <span className="text-fg-muted tnum">
            {plural(r.assignmentCount ?? 0, m.adm_role_assignments_one, m.adm_role_assignments)}
          </span>
        ),
    },
  ];

  return (
    <Page
      title={m.adm_nav_roles()}
      icon={Shield}
      meta={m.adm_roles_meta()}
      width="full"
      className="max-w-5xl"
      actions={
        <Button variant="primary" onPress={() => open("new")}>
          <Plus size={14} aria-hidden="true" />
          {m.adm_role_new()}
        </Button>
      }
    >
      <QueryBoundary query={q} skeleton={<TableSkeleton rows={5} columns={3} />}>
        {(roles) => (
          <DataTable label={m.adm_nav_roles()} columns={columns} rows={roles} onRowAction={(r) => open(r.id)} />
        )}
      </QueryBoundary>
      <RoleDrawer
        id={openId}
        template={template}
        canManage={caps.users}
        onClose={() => {
          setOpenId(null);
          setTemplate(null);
        }}
        onOpen={(id) => open(id)}
        onDuplicate={(role) => open("new", role)}
      />
    </Page>
  );
}
